/* eslint-disable no-unused-vars */
// =============================================================================
// CampaignDetailsScreen.js — FULL REBUILD
// JobEng — Business / Marketing
// Location: app/screens/business/marketing/CampaignDetailsScreen.js
// Receives: route.params.campaign and/or route.params.campaignId
//
// Live DB: GET/PUT/DELETE /api/data/campaigns/:id ·
//          GET /api/data/campaign_content?campaign_id= (content library)
// DB monitor: /health + table ping badge
// Tabs: Overview · Performance · Content · Settings
// NEW: proper Targeting entry point (passes campaign + campaignId — fixes the
//      "no campaign id reached this screen" error), budget pacing indicator,
//      AI performance analysis, live content library, duplicate/complete
//      actions, cost-per-conversion, days-remaining countdown.
// =============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Alert,
  RefreshControl,
  Share,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Clipboard from 'expo-clipboard';

// ─── API ─────────────────────────────────────────────────────────────────────

const API_BASE = 'https://api.jobeng.ai';
const API_KEY =
  '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const getH = async () => {
  const tok =
    (await AsyncStorage.getItem('auth_token').catch(() => null)) ||
    (await AsyncStorage.getItem('userToken').catch(() => null));
  return {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY,
    ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const C = {
  primary: '#0e4e96',
  primaryDark: '#093a72',
  primaryLight: '#1e7be2',
  accent: '#EC4899',
  bg: '#F4F6FB',
  card: '#FFFFFF',
  text: '#0F172A',
  sub: '#64748B',
  border: '#E2E8F0',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  purple: '#7C3AED',
  teal: '#0891B2',
};

const STATUS_META = {
  draft: { label: 'Draft', color: '#94A3B8' },
  active: { label: 'Active', color: C.success },
  paused: { label: 'Paused', color: C.warning },
  completed: { label: 'Completed', color: '#6366F1' },
};

const CHANNEL_META = {
  email: { label: 'Email', icon: 'email-outline', color: C.teal },
  social: { label: 'Social', icon: 'share-variant-outline', color: C.accent },
  job_board: { label: 'Job Boards', icon: 'briefcase-outline', color: C.primary },
  sms: { label: 'SMS', icon: 'message-text-outline', color: C.success },
  ppc: { label: 'Paid Ads', icon: 'cursor-default-click-outline', color: C.warning },
  content: { label: 'Content', icon: 'post-outline', color: C.purple },
};

const TABS = [
  { k: 'overview', l: 'Overview', i: 'view-dashboard-outline' },
  { k: 'performance', l: 'Performance', i: 'chart-line' },
  { k: 'content', l: 'Content', i: 'palette-outline' },
  { k: 'settings', l: 'Settings', i: 'cog-outline' },
];

const toNum = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const money = (n) => `£${toNum(n).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
const compact = (n) => {
  const v = toNum(n);
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return String(v);
};
const fmtDate = (d) => (d ? String(d).slice(0, 10) : '—');

const parseChannels = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try { const j = JSON.parse(raw); if (Array.isArray(j)) return j; } catch {}
    return raw.split(',').map((x) => x.trim()).filter(Boolean);
  }
  return [];
};

const parseTargeting = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return null; } }
  return null;
};

const norm = (c) => {
  const impressions = toNum(c.impressions || c.reach || c.total_reach);
  const clicks = toNum(c.clicks);
  const conversions = toNum(c.conversions);
  return {
    id: c.id || null,
    name: c.name || c.campaign_name || 'Campaign',
    description: c.description || '',
    objective: c.objective || c.type || '',
    status: String(c.status || 'draft').toLowerCase(),
    channels: parseChannels(c.channels || c.channel || c.platforms),
    budget: toNum(c.budget),
    spent: toNum(c.spent || c.spend),
    impressions,
    clicks,
    conversions,
    target_audience: c.target_audience || c.targetAudience || '',
    targeting: parseTargeting(c.targeting),
    start_date: c.start_date ? String(c.start_date).slice(0, 10) : '',
    end_date: c.end_date ? String(c.end_date).slice(0, 10) : '',
    created_at: c.created_at || '',
    _raw: c,
  };
};

// =============================================================================
// MODULE-SCOPE COMPONENTS (React.memo)
// =============================================================================

const DbBadge = React.memo(({ status, onPress }) => {
  const cfg =
    status === 'online' ? { c: '#34D399', t: 'Live' }
    : status === 'offline' ? { c: '#F87171', t: 'Offline' }
    : { c: '#FBBF24', t: '…' };
  return (
    <TouchableOpacity style={s.dbBadge} onPress={onPress} activeOpacity={0.7}>
      <View style={[s.dbDot, { backgroundColor: cfg.c }]} />
      <Text style={s.dbText}>{cfg.t}</Text>
    </TouchableOpacity>
  );
});

const KpiTile = React.memo(({ icon, label, value, sub, color }) => (
  <View style={s.kpi}>
    <View style={[s.kpiIcon, { backgroundColor: `${color}15` }]}>
      <MaterialCommunityIcons name={icon} size={16} color={color} />
    </View>
    <Text style={s.kpiValue} numberOfLines={1}>{value}</Text>
    <Text style={s.kpiLabel} numberOfLines={1}>{label}</Text>
    {sub ? <Text style={[s.kpiSub, { color }]} numberOfLines={1}>{sub}</Text> : null}
  </View>
));

const Card = React.memo(({ title, right, children }) => (
  <View style={s.card}>
    {(title || right) && (
      <View style={s.cardHead}>
        <Text style={s.cardTitle}>{title}</Text>
        {right || null}
      </View>
    )}
    {children}
  </View>
));

const Row = React.memo(({ label, value }) => (
  <View style={s.row}>
    <Text style={s.rowLbl}>{label}</Text>
    <Text style={s.rowVal} numberOfLines={2}>{value}</Text>
  </View>
));

const Bar = React.memo(({ label, value, max, color, display }) => (
  <View style={s.barRow}>
    <Text style={s.barLabel} numberOfLines={1}>{label}</Text>
    <View style={s.barTrack}>
      <View style={[s.barFill, { width: `${max > 0 ? Math.max(2, (value / max) * 100) : 2}%`, backgroundColor: color }]} />
    </View>
    <Text style={s.barValue}>{display}</Text>
  </View>
));

const ActionBtn = React.memo(({ icon, label, color, onPress, outline }) => (
  <TouchableOpacity
    style={[s.fullBtn, outline ? { backgroundColor: '#fff', borderWidth: 1.5, borderColor: color } : { backgroundColor: color }]}
    onPress={onPress}
    activeOpacity={0.8}
  >
    <MaterialCommunityIcons name={icon} size={17} color={outline ? color : '#fff'} />
    <Text style={[s.fullBtnTxt, outline && { color }]}>{label}</Text>
  </TouchableOpacity>
));

const ContentItem = React.memo(({ item, onCopy }) => (
  <View style={s.contentItem}>
    <View style={s.contentIcon}>
      <MaterialCommunityIcons name="text-box-outline" size={17} color={C.primary} />
    </View>
    <View style={{ flex: 1, marginHorizontal: 10 }}>
      <Text style={s.contentType}>{item.content_type || item.type || 'content'}{item.tone ? ` · ${item.tone}` : ''}</Text>
      <Text style={s.contentPreview} numberOfLines={2}>{item.content || item.title || ''}</Text>
    </View>
    {!!item.content && (
      <TouchableOpacity onPress={() => onCopy(item.content)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
        <Ionicons name="copy-outline" size={16} color={C.sub} />
      </TouchableOpacity>
    )}
  </View>
));

// =============================================================================
// MAIN SCREEN
// =============================================================================

const CampaignDetailsScreen = ({ navigation, route }) => {
  const rawCampaign = route?.params?.campaign || {};
  const campaignId =
    route?.params?.campaignId || rawCampaign?.id || rawCampaign?.campaign_id || null;

  const [campaign, setCampaign] = useState(() => norm(rawCampaign));
  const [loading, setLoading] = useState(!!campaignId);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const [contentItems, setContentItems] = useState([]);
  const [contentLoading, setContentLoading] = useState(false);

  const [aiAnalysis, setAiAnalysis] = useState('');
  const [aiBusy, setAiBusy] = useState(false);

  const [dbStatus, setDbStatus] = useState('checking');
  const [dbDetail, setDbDetail] = useState('');

  // ─── DB CHECK ────────────────────────────────────────────────────────────

  const checkDb = useCallback(async () => {
    setDbStatus('checking');
    try {
      const h = await getH();
      const t0 = Date.now();
      const health = await fetch(`${API_BASE}/health`, { headers: h }).then((r) => r.ok).catch(() => false);
      const table = await fetch(`${API_BASE}/api/data/campaigns?limit=1`, { headers: h }).then((r) => r.ok).catch(() => false);
      const ms = Date.now() - t0;
      if (health && table) { setDbStatus('online'); setDbDetail(`API + campaigns table reachable · ${ms}ms`); }
      else if (health) { setDbStatus('offline'); setDbDetail('API up, campaigns table unreachable'); }
      else { setDbStatus('offline'); setDbDetail('API not responding — check PM2 / nginx'); }
    } catch (e) { setDbStatus('offline'); setDbDetail(e?.message || 'Network error'); }
  }, []);

  const showDbDetail = useCallback(() => {
    Alert.alert(
      dbStatus === 'online' ? '✅ Connected' : '⚠️ Connection issue',
      dbDetail || 'Checking…',
      [{ text: 'Re-check', onPress: checkDb }, { text: 'OK' }]
    );
  }, [dbStatus, dbDetail, checkDb]);

  // ─── LOAD ────────────────────────────────────────────────────────────────

  const loadCampaign = useCallback(async (spinner = true) => {
    if (!campaignId) { setLoading(false); setRefreshing(false); return; }
    if (spinner) setLoading(true);
    try {
      const h = await getH();
      const res = await fetch(`${API_BASE}/api/data/campaigns/${campaignId}`, { headers: h })
        .then((r) => r.json()).catch(() => null);
      const row = res?.data && !Array.isArray(res.data) ? res.data : Array.isArray(res?.data) ? res.data[0] : null;
      if (row) setCampaign(norm(row));
    } catch {}
    setLoading(false);
    setRefreshing(false);
  }, [campaignId]);

  const loadContent = useCallback(async () => {
    if (!campaignId) return;
    setContentLoading(true);
    let loaded = false;
    try {
      const h = await getH();
      const res = await fetch(`${API_BASE}/api/data/campaign_content?campaign_id=${campaignId}&limit=30`, { headers: h })
        .then((r) => r.json()).catch(() => null);
      const rows = Array.isArray(res?.data) ? res.data : null;
      if (rows) {
        setContentItems(rows.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || ''))));
        loaded = true;
      }
    } catch {}
    if (!loaded) {
      const raw = await AsyncStorage.getItem(`campaign_content_${campaignId}`).catch(() => null);
      if (raw) { try { setContentItems(JSON.parse(raw)); } catch {} }
    }
    setContentLoading(false);
  }, [campaignId]);

  useEffect(() => {
    checkDb();
    loadCampaign();
    loadContent();
    const unsub = navigation?.addListener?.('focus', () => { loadCampaign(false); loadContent(); });
    return unsub;
  }, [checkDb, loadCampaign, loadContent, navigation]);

  const refresh = useCallback(() => { setRefreshing(true); checkDb(); loadCampaign(false); loadContent(); }, [checkDb, loadCampaign, loadContent]);

  // ─── NAVIGATION (always pass campaign AND campaignId) ───────────────────

  const navParams = useMemo(() => ({ campaign: campaign._raw || rawCampaign, campaignId }), [campaign, rawCampaign, campaignId]);

  const goAnalytics = useCallback(() => navigation.navigate('CampaignAnalyticsDashboard', navParams), [navigation, navParams]);
  const goAI = useCallback((contentType) => navigation.navigate('CampaignAIContentAssistantScreen', { ...navParams, contentType }), [navigation, navParams]);
  const goEdit = useCallback(() => navigation.navigate('EditCampaign', navParams), [navigation, navParams]);
  const goTargeting = useCallback(() => navigation.navigate('TargetingSettings', navParams), [navigation, navParams]);
  const goAddAsset = useCallback(() => navigation.navigate('AddCampaignAsset', navParams), [navigation, navParams]);
  const goVideo = useCallback(() => navigation.navigate('VideoEditor', navParams), [navigation, navParams]);
  const goExport = useCallback((type) => navigation.navigate('ExportCampaignScreen', { type, ...navParams }), [navigation, navParams]);

  // ─── ACTIONS ─────────────────────────────────────────────────────────────

  const patchCampaign = useCallback(async (patch, optimistic) => {
    if (!campaignId) { Alert.alert('No campaign id', 'This campaign has no server id.'); return false; }
    const before = campaign;
    if (optimistic) setCampaign((prev) => ({ ...prev, ...optimistic }));
    try {
      const h = await getH();
      const res = await fetch(`${API_BASE}/api/data/campaigns/${campaignId}`, {
        method: 'PUT', headers: h, body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return true;
    } catch (e) {
      setCampaign(before);
      Alert.alert('Update failed', e?.message || 'Could not update campaign.');
      return false;
    }
  }, [campaignId, campaign]);

  const handlePauseResume = useCallback(() => {
    const next = campaign.status === 'active' ? 'paused' : 'active';
    patchCampaign({ status: next }, { status: next });
  }, [campaign.status, patchCampaign]);

  const handleComplete = useCallback(() => {
    Alert.alert('Complete campaign', `Mark "${campaign.name}" as completed?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Complete', onPress: () => patchCampaign({ status: 'completed' }, { status: 'completed' }) },
    ]);
  }, [campaign.name, patchCampaign]);

  const handleDuplicate = useCallback(async () => {
    try {
      const h = await getH();
      const r = campaign._raw || {};
      const res = await fetch(`${API_BASE}/api/data/campaigns`, {
        method: 'POST', headers: h,
        body: JSON.stringify({
          company_id: r.company_id, name: `${campaign.name} (Copy)`,
          description: r.description || '', objective: r.objective || '',
          channels: typeof r.channels === 'string' ? r.channels : JSON.stringify(campaign.channels),
          target_audience: r.target_audience || '', budget: campaign.budget,
          status: 'draft', spent: 0, impressions: 0, clicks: 0, conversions: 0,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      Alert.alert('✅ Duplicated', 'A draft copy was created.', [{ text: 'OK', onPress: () => navigation.goBack() }]);
    } catch (e) { Alert.alert('Duplicate failed', e?.message || 'Could not duplicate.'); }
  }, [campaign, navigation]);

  const handleDelete = useCallback(() => {
    Alert.alert('Delete campaign', `Delete "${campaign.name}"? This cannot be undone.`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete', style: 'destructive',
        onPress: async () => {
          try {
            const h = await getH();
            await fetch(`${API_BASE}/api/data/campaigns/${campaignId}`, { method: 'DELETE', headers: h });
            navigation.goBack();
          } catch { Alert.alert('Delete failed', 'Could not delete campaign.'); }
        },
      },
    ]);
  }, [campaign.name, campaignId, navigation]);

  const shareSummary = useCallback(() => {
    Share.share({
      message: `📊 ${campaign.name}\nStatus: ${STATUS_META[campaign.status]?.label || campaign.status}\nBudget: ${money(campaign.budget)} | Spent: ${money(campaign.spent)}\nReach: ${compact(campaign.impressions)} | Clicks: ${compact(campaign.clicks)} | Conversions: ${campaign.conversions}`,
    }).catch(() => {});
  }, [campaign]);

  const copyText = useCallback(async (text) => {
    try {
      await Clipboard.setStringAsync(text);
      Alert.alert('Copied', 'Content copied to clipboard.');
    } catch { Share.share({ message: text }).catch(() => {}); }
  }, []);

  // ─── AI ANALYSIS ─────────────────────────────────────────────────────────

  const analyse = useCallback(async () => {
    setAiBusy(true);
    try {
      const h = await getH();
      const prompt =
        `You are a UK marketing analyst. Analyse this campaign and give a 2-sentence verdict plus 3 prioritised recommendations (plain text, British English, no markdown):\n` +
        `Name: ${campaign.name}\nStatus: ${campaign.status}\nObjective: ${campaign.objective}\n` +
        `Budget £${campaign.budget}, spent £${campaign.spent}\nReach ${campaign.impressions}, clicks ${campaign.clicks}, conversions ${campaign.conversions}\n` +
        `Channels: ${campaign.channels.join(', ') || 'none'}\nDates: ${campaign.start_date || 'n/a'} → ${campaign.end_date || 'n/a'}`;
      const res = await fetch(`${API_BASE}/api/ai`, {
        method: 'POST', headers: h, body: JSON.stringify({ prompt }),
      }).then((r) => r.json()).catch(() => null);
      if (!res?.content) throw new Error(res?.message || 'No response');
      setAiAnalysis(String(res.content).trim());
    } catch (e) {
      Alert.alert('AI unavailable', e?.message || 'Could not analyse.');
    } finally { setAiBusy(false); }
  }, [campaign]);

  // ─── DERIVED ─────────────────────────────────────────────────────────────

  const si = STATUS_META[campaign.status] || STATUS_META.draft;
  const ctr = campaign.impressions > 0 ? ((campaign.clicks / campaign.impressions) * 100).toFixed(1) : '0.0';
  const convRate = campaign.clicks > 0 ? ((campaign.conversions / campaign.clicks) * 100).toFixed(1) : '0.0';
  const costPerConv = campaign.conversions > 0 ? campaign.spent / campaign.conversions : 0;
  const budgetPct = campaign.budget > 0 ? Math.min(100, (campaign.spent / campaign.budget) * 100) : 0;
  const overBudget = campaign.budget > 0 && campaign.spent > campaign.budget;

  const pacing = useMemo(() => {
    if (!campaign.start_date || !campaign.end_date || campaign.budget <= 0) return null;
    const start = new Date(campaign.start_date).getTime();
    const end = new Date(campaign.end_date).getTime();
    const now = Date.now();
    if (!(end > start)) return null;
    const timePct = Math.max(0, Math.min(100, ((now - start) / (end - start)) * 100));
    const spendPctRaw = (campaign.spent / campaign.budget) * 100;
    const diff = spendPctRaw - timePct;
    const daysLeft = Math.max(0, Math.ceil((end - now) / 86400000));
    const state =
      now < start ? { txt: 'Not started', color: C.sub }
      : now > end ? { txt: 'Ended', color: C.sub }
      : diff > 15 ? { txt: 'Overspending', color: C.danger }
      : diff < -15 ? { txt: 'Underspending', color: C.warning }
      : { txt: 'On pace', color: C.success };
    return { timePct, daysLeft, state };
  }, [campaign]);

  const targetingSummary = useMemo(() => {
    const tg = campaign.targeting;
    if (!tg) return [];
    const bits = [];
    if (tg.ageRanges?.length) bits.push(`Ages ${tg.ageRanges.join(', ')}`);
    if (tg.cities?.length) bits.push(tg.cities.slice(0, 3).join(', ') + (tg.cities.length > 3 ? ` +${tg.cities.length - 3}` : ''));
    if (tg.jobTitles?.length) bits.push(tg.jobTitles.slice(0, 3).join(', '));
    if (tg.industries?.length) bits.push(tg.industries.slice(0, 2).join(', '));
    return bits;
  }, [campaign.targeting]);

  // ─── TAB RENDERERS ───────────────────────────────────────────────────────

  const renderOverview = () => (
    <View>
      <View style={s.kpiGrid}>
        <KpiTile icon="eye-outline" label="Reach" value={compact(campaign.impressions)} color={C.purple} />
        <KpiTile icon="cursor-default-click-outline" label="Clicks" value={compact(campaign.clicks)} sub={`${ctr}% CTR`} color={C.primaryLight} />
        <KpiTile icon="target" label="Conversions" value={compact(campaign.conversions)} sub={`${convRate}% rate`} color={C.success} />
        <KpiTile icon="cash-multiple" label="Cost / conv." value={campaign.conversions > 0 ? money(costPerConv) : '—'} color={C.warning} />
      </View>

      {!!campaign.description && (
        <Card title="About">
          <Text style={s.bodyText}>{campaign.description}</Text>
        </Card>
      )}

      <Card title="Channels">
        {campaign.channels.length === 0 ? (
          <Text style={s.muted}>No channels assigned — edit the campaign to add some.</Text>
        ) : (
          <View style={s.channelWrap}>
            {campaign.channels.map((ch) => {
              const cm = CHANNEL_META[ch] || { label: ch, icon: 'tag-outline', color: C.sub };
              return (
                <View key={ch} style={[s.channelBadge, { backgroundColor: `${cm.color}12` }]}>
                  <MaterialCommunityIcons name={cm.icon} size={13} color={cm.color} />
                  <Text style={[s.channelText, { color: cm.color }]}>{cm.label}</Text>
                </View>
              );
            })}
          </View>
        )}
      </Card>

      <Card
        title="Audience targeting"
        right={
          <TouchableOpacity onPress={goTargeting}>
            <Text style={s.linkText}>{campaign.targeting ? 'Edit' : 'Set up'}</Text>
          </TouchableOpacity>
        }
      >
        {targetingSummary.length === 0 ? (
          <Text style={s.muted}>
            {campaign.target_audience || 'No targeting configured yet — tap "Set up" to define your audience.'}
          </Text>
        ) : (
          <View style={s.channelWrap}>
            {targetingSummary.map((b) => (
              <View key={b} style={s.targetChip}><Text style={s.targetChipText}>{b}</Text></View>
            ))}
          </View>
        )}
      </Card>

      <Card title="Schedule">
        <Row label="Start" value={fmtDate(campaign.start_date)} />
        <Row label="End" value={fmtDate(campaign.end_date)} />
        {pacing && pacing.daysLeft > 0 && campaign.status === 'active' && (
          <Row label="Days remaining" value={`${pacing.daysLeft} day${pacing.daysLeft === 1 ? '' : 's'}`} />
        )}
      </Card>
    </View>
  );

  const renderPerformance = () => (
    <View>
      <Card title="Budget pacing" right={pacing ? <Text style={[s.paceState, { color: pacing.state.color }]}>{pacing.state.txt}</Text> : null}>
        <Bar label="Spend" value={campaign.spent} max={Math.max(campaign.budget, campaign.spent, 1)} color={overBudget ? C.danger : C.primaryLight} display={money(campaign.spent)} />
        <Bar label="Budget" value={campaign.budget} max={Math.max(campaign.budget, campaign.spent, 1)} color="#CBD5E1" display={money(campaign.budget)} />
        {pacing && (
          <>
            <Bar label="Time elapsed" value={pacing.timePct} max={100} color={C.purple} display={`${pacing.timePct.toFixed(0)}%`} />
            <Text style={s.muted}>
              {pacing.state.txt === 'Overspending'
                ? 'Spend is ahead of the timeline — budget will run out before the end date.'
                : pacing.state.txt === 'Underspending'
                ? 'Spend is behind the timeline — consider increasing activity.'
                : pacing.state.txt === 'On pace'
                ? 'Spend is tracking the campaign timeline.'
                : ''}
            </Text>
          </>
        )}
      </Card>

      <Card title="Funnel">
        <Bar label="Reach" value={campaign.impressions} max={Math.max(campaign.impressions, 1)} color={C.purple} display={compact(campaign.impressions)} />
        <Bar label="Clicks" value={campaign.clicks} max={Math.max(campaign.impressions, 1)} color={C.primaryLight} display={compact(campaign.clicks)} />
        <Bar label="Conversions" value={campaign.conversions} max={Math.max(campaign.impressions, 1)} color={C.success} display={compact(campaign.conversions)} />
        <View style={{ flexDirection: 'row', justifyContent: 'space-around', marginTop: 10 }}>
          <Text style={s.funnelStat}>CTR <Text style={{ color: C.primaryLight }}>{ctr}%</Text></Text>
          <Text style={s.funnelStat}>Conv. rate <Text style={{ color: C.success }}>{convRate}%</Text></Text>
          <Text style={s.funnelStat}>CPC <Text style={{ color: C.warning }}>{campaign.clicks > 0 ? money(campaign.spent / campaign.clicks) : '—'}</Text></Text>
        </View>
      </Card>

      <Card title="AI analysis">
        <TouchableOpacity style={[s.aiBtn, aiBusy && { opacity: 0.6 }]} onPress={analyse} disabled={aiBusy}>
          {aiBusy ? <ActivityIndicator size="small" color="#fff" /> : <MaterialCommunityIcons name="brain" size={16} color="#fff" />}
          <Text style={s.aiBtnText}>{aiBusy ? 'Analysing…' : aiAnalysis ? 'Re-analyse' : 'Analyse performance'}</Text>
        </TouchableOpacity>
        {!!aiAnalysis && <Text style={[s.bodyText, { marginTop: 12 }]}>{aiAnalysis}</Text>}
      </Card>

      <ActionBtn icon="chart-bar" label="Open Full Analytics Dashboard" color={C.purple} onPress={goAnalytics} />
    </View>
  );

  const renderContent = () => (
    <View>
      <Card title="Create content">
        <View style={s.createGrid}>
          {[
            { l: 'Social', i: 'share-variant', c: C.accent, fn: () => goAI('social') },
            { l: 'Email', i: 'email-edit-outline', c: C.teal, fn: () => goAI('email') },
            { l: 'Ad copy', i: 'bullhorn-outline', c: C.warning, fn: () => goAI('ad') },
            { l: 'Job post', i: 'briefcase-outline', c: C.primary, fn: () => goAI('job') },
            { l: 'Asset', i: 'image-plus', c: C.purple, fn: goAddAsset },
            { l: 'Video', i: 'movie-open-edit-outline', c: C.danger, fn: goVideo },
          ].map((a) => (
            <TouchableOpacity key={a.l} style={s.createBtn} onPress={a.fn} activeOpacity={0.7}>
              <View style={[s.createIcon, { backgroundColor: `${a.c}15` }]}>
                <MaterialCommunityIcons name={a.i} size={19} color={a.c} />
              </View>
              <Text style={s.createLabel}>{a.l}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </Card>

      <Card
        title={`Content library (${contentItems.length})`}
        right={
          <TouchableOpacity onPress={loadContent}>
            <Ionicons name="refresh" size={16} color={C.primary} />
          </TouchableOpacity>
        }
      >
        {contentLoading ? (
          <ActivityIndicator color={C.primary} style={{ marginVertical: 16 }} />
        ) : contentItems.length === 0 ? (
          <Text style={s.muted}>Nothing here yet — content you save in the AI Assistant appears here.</Text>
        ) : (
          contentItems.slice(0, 15).map((item, i) => (
            <ContentItem key={String(item.id || i)} item={item} onCopy={copyText} />
          ))
        )}
      </Card>

      <Card title="Exports">
        <View style={{ gap: 0 }}>
          <ActionBtn icon="file-pdf-box" label="Campaign Report (PDF)" color={C.danger} outline onPress={() => goExport('report')} />
          <ActionBtn icon="book-open-variant" label="Marketing Brochure" color={C.purple} outline onPress={() => goExport('brochure')} />
          <ActionBtn icon="newspaper-variant-outline" label="Print Flyer" color={C.teal} outline onPress={() => goExport('flyer')} />
        </View>
      </Card>
    </View>
  );

  const renderSettings = () => (
    <View>
      <Card title="Campaign details">
        <Row label="Status" value={si.label} />
        {!!campaign.objective && <Row label="Objective" value={campaign.objective} />}
        <Row label="Start date" value={fmtDate(campaign.start_date)} />
        <Row label="End date" value={fmtDate(campaign.end_date)} />
        <Row label="Budget" value={money(campaign.budget)} />
        <Row label="Spent" value={money(campaign.spent)} />
        {!!campaign.target_audience && <Row label="Audience" value={campaign.target_audience} />}
        {campaign.channels.length > 0 && <Row label="Channels" value={campaign.channels.map((ch) => CHANNEL_META[ch]?.label || ch).join(', ')} />}
      </Card>

      <View>
        <ActionBtn icon="pencil" label="Edit Campaign" color={C.primary} onPress={goEdit} />
        <ActionBtn icon="target" label="Audience Targeting" color={C.purple} onPress={goTargeting} />
        {campaign.status !== 'completed' && (
          <ActionBtn
            icon={campaign.status === 'active' ? 'pause-circle' : 'play-circle'}
            label={campaign.status === 'active' ? 'Pause Campaign' : 'Activate Campaign'}
            color={campaign.status === 'active' ? C.warning : C.success}
            onPress={handlePauseResume}
          />
        )}
        {campaign.status === 'active' && (
          <ActionBtn icon="flag-checkered" label="Mark as Completed" color="#6366F1" onPress={handleComplete} />
        )}
        <ActionBtn icon="content-copy" label="Duplicate Campaign" color={C.teal} outline onPress={handleDuplicate} />
        <ActionBtn icon="share-variant" label="Share Summary" color={C.sub} outline onPress={shareSummary} />
        <ActionBtn icon="delete" label="Delete Campaign" color={C.danger} onPress={handleDelete} />
      </View>
    </View>
  );

  // ─── RENDER ──────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <SafeAreaView style={[s.safe, { justifyContent: 'center', alignItems: 'center', backgroundColor: C.bg }]} edges={['top']}>
        <ActivityIndicator size="large" color={C.primary} />
        <Text style={{ color: C.sub, marginTop: 12 }}>Loading campaign…</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={s.safe} edges={['top']}>
      {/* Header */}
      <View style={s.header}>
        <TouchableOpacity style={s.headerBtn} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={22} color="#fff" />
        </TouchableOpacity>
        <View style={{ flex: 1, marginLeft: 4 }}>
          <Text style={s.headerTitle} numberOfLines={1}>{campaign.name}</Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 2 }}>
            <View style={[s.statusBadge, { backgroundColor: `${si.color}40` }]}>
              <Text style={s.statusText}>{si.label}</Text>
            </View>
            {!!campaign.objective && (
              <Text style={s.headerSub} numberOfLines={1}>{campaign.objective}</Text>
            )}
          </View>
        </View>
        <DbBadge status={dbStatus} onPress={showDbDetail} />
        <TouchableOpacity style={s.headerBtn} onPress={shareSummary}>
          <MaterialCommunityIcons name="export-variant" size={19} color="#fff" />
        </TouchableOpacity>
      </View>

      {/* Budget bar in header */}
      <View style={s.headerBudget}>
        <View style={s.headerBudgetTrack}>
          <View style={[s.headerBudgetFill, { width: `${budgetPct}%`, backgroundColor: overBudget ? '#F87171' : '#7DB4F5' }]} />
        </View>
        <Text style={s.headerBudgetText}>
          {money(campaign.spent)} / {money(campaign.budget)} ({budgetPct.toFixed(0)}%)
        </Text>
      </View>

      {/* Tabs */}
      <View style={s.tabBar}>
        {TABS.map((tb) => (
          <TouchableOpacity key={tb.k} style={[s.tabBtn, activeTab === tb.k && s.tabBtnActive]} onPress={() => setActiveTab(tb.k)}>
            <MaterialCommunityIcons name={tb.i} size={15} color={activeTab === tb.k ? C.primary : 'rgba(255,255,255,0.75)'} />
            <Text style={[s.tabText, activeTab === tb.k && s.tabTextActive]}>{tb.l}</Text>
          </TouchableOpacity>
        ))}
      </View>

      <ScrollView
        style={s.body}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={refresh} tintColor={C.primary} />}
        showsVerticalScrollIndicator={false}
      >
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'performance' && renderPerformance()}
        {activeTab === 'content' && renderContent()}
        {activeTab === 'settings' && renderSettings()}
        <View style={{ height: 36 }} />
      </ScrollView>
    </SafeAreaView>
  );
};

// =============================================================================
// STYLES
// =============================================================================

const s = StyleSheet.create({
  safe: { flex: 1, backgroundColor: C.primary },
  header: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.primary, paddingHorizontal: 10, paddingVertical: 10, gap: 6 },
  headerBtn: { padding: 5 },
  headerTitle: { color: '#fff', fontSize: 16.5, fontWeight: '800' },
  headerSub: { color: 'rgba(255,255,255,0.65)', fontSize: 10.5, flexShrink: 1 },
  statusBadge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 9 },
  statusText: { fontSize: 9.5, fontWeight: '800', color: '#fff' },

  dbBadge: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: 'rgba(255,255,255,0.14)', borderRadius: 12, paddingHorizontal: 9, paddingVertical: 5 },
  dbDot: { width: 7, height: 7, borderRadius: 4 },
  dbText: { color: '#fff', fontSize: 10.5, fontWeight: '700' },

  headerBudget: { backgroundColor: C.primary, paddingHorizontal: 14, paddingBottom: 10 },
  headerBudgetTrack: { height: 6, backgroundColor: 'rgba(255,255,255,0.18)', borderRadius: 3, overflow: 'hidden' },
  headerBudgetFill: { height: 6, borderRadius: 3 },
  headerBudgetText: { color: 'rgba(255,255,255,0.8)', fontSize: 10.5, fontWeight: '700', marginTop: 5 },

  tabBar: { flexDirection: 'row', backgroundColor: C.primary, paddingHorizontal: 8, paddingBottom: 8, gap: 5 },
  tabBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4, paddingVertical: 8, borderRadius: 11, backgroundColor: 'rgba(255,255,255,0.1)' },
  tabBtnActive: { backgroundColor: '#fff' },
  tabText: { fontSize: 10, fontWeight: '700', color: 'rgba(255,255,255,0.8)' },
  tabTextActive: { color: C.primary },

  body: { flex: 1, backgroundColor: C.bg, paddingHorizontal: 14, paddingTop: 12 },

  kpiGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 4 },
  kpi: { width: '48%', flexGrow: 1, backgroundColor: C.card, borderRadius: 13, padding: 12, borderWidth: 1, borderColor: C.border },
  kpiIcon: { width: 28, height: 28, borderRadius: 9, alignItems: 'center', justifyContent: 'center', marginBottom: 6 },
  kpiValue: { fontSize: 16.5, fontWeight: '800', color: C.text },
  kpiLabel: { fontSize: 10.5, color: C.sub, marginTop: 1 },
  kpiSub: { fontSize: 10, fontWeight: '700', marginTop: 2 },

  card: { backgroundColor: C.card, borderRadius: 14, padding: 14, marginTop: 10, borderWidth: 1, borderColor: C.border },
  cardHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 9 },
  cardTitle: { fontSize: 13.5, fontWeight: '800', color: C.text },
  linkText: { fontSize: 12, fontWeight: '800', color: C.primary },

  bodyText: { fontSize: 13, color: C.text, lineHeight: 19 },
  muted: { fontSize: 12.5, color: C.sub, lineHeight: 18, marginTop: 4 },

  channelWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 7 },
  channelBadge: { flexDirection: 'row', alignItems: 'center', gap: 4, borderRadius: 8, paddingHorizontal: 9, paddingVertical: 5 },
  channelText: { fontSize: 11, fontWeight: '700' },

  targetChip: { backgroundColor: '#F3E8FF', borderRadius: 8, paddingHorizontal: 9, paddingVertical: 5 },
  targetChipText: { fontSize: 11, fontWeight: '700', color: C.purple },

  row: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6, gap: 12 },
  rowLbl: { fontSize: 12.5, color: C.sub, fontWeight: '600' },
  rowVal: { fontSize: 12.5, color: C.text, fontWeight: '700', flexShrink: 1, textAlign: 'right' },

  barRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 5, gap: 8 },
  barLabel: { width: 84, fontSize: 11.5, fontWeight: '700', color: C.text },
  barTrack: { flex: 1, height: 8, backgroundColor: '#EEF2F8', borderRadius: 4, overflow: 'hidden' },
  barFill: { height: 8, borderRadius: 4 },
  barValue: { width: 56, fontSize: 11, fontWeight: '700', color: C.sub, textAlign: 'right' },
  paceState: { fontSize: 11.5, fontWeight: '800' },
  funnelStat: { fontSize: 11.5, fontWeight: '700', color: C.sub },

  aiBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, backgroundColor: C.accent, borderRadius: 11, paddingVertical: 11 },
  aiBtnText: { color: '#fff', fontSize: 13, fontWeight: '800' },

  createGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  createBtn: { width: '31%', flexGrow: 1, alignItems: 'center', paddingVertical: 10, backgroundColor: '#F8FAFD', borderRadius: 12, borderWidth: 1, borderColor: C.border },
  createIcon: { width: 36, height: 36, borderRadius: 11, alignItems: 'center', justifyContent: 'center', marginBottom: 5 },
  createLabel: { fontSize: 10.5, fontWeight: '700', color: C.text },

  contentItem: { flexDirection: 'row', alignItems: 'center', paddingVertical: 9, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  contentIcon: { width: 32, height: 32, borderRadius: 9, backgroundColor: '#EFF4FF', alignItems: 'center', justifyContent: 'center' },
  contentType: { fontSize: 11, fontWeight: '800', color: C.primary, textTransform: 'capitalize' },
  contentPreview: { fontSize: 11.5, color: C.sub, marginTop: 2, lineHeight: 15 },

  fullBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7, borderRadius: 12, paddingVertical: 13, marginTop: 10 },
  fullBtnTxt: { color: '#fff', fontSize: 13.5, fontWeight: '800' },
});

export default CampaignDetailsScreen;
